import React, { useEffect, useState, useCallback } from 'react';
import Nango from '@nangohq/frontend';
import {
  CheckCircle2, RefreshCw, Zap, Key, MessageCircle, Clock, Trophy,
  Wifi, WifiOff, ChevronDown, Shield, AlertTriangle, ExternalLink,
  ToggleLeft, ToggleRight, Users,
} from 'lucide-react';
import { toast } from '../ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─────────────────────────────────────────────
// Tarjeta unificada: icono + nombre + estado + control único
// ─────────────────────────────────────────────
const ConnectionCard = ({ icon, name, subtitle, status, control, testId, badge }) => (
  <div
    className="flex items-center justify-between rounded-2xl border border-border-subtle bg-card/60 px-5 py-4 backdrop-blur-sm transition hover:border-primary/30"
    data-testid={testId}
  >
    <div className="flex items-center gap-4 min-w-0">
      <div className="flex-shrink-0 flex h-10 w-10 items-center justify-center rounded-xl bg-background/80 text-text-heading">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-semibold text-sm text-text-heading">{name}</p>
          {badge && <span className="rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] text-primary">{badge}</span>}
        </div>
        {subtitle && <p className="text-[11px] text-text-muted mt-0.5">{subtitle}</p>}
      </div>
    </div>
    <div className="flex-shrink-0 flex items-center gap-3 ml-4">
      {status === 'connected' ? (
        <span className="hidden sm:flex items-center gap-1 text-[11px] text-emerald-400 font-medium">
          <CheckCircle2 size={11} /> Conectado
        </span>
      ) : status === 'pending_meta' ? (
        <span className="hidden sm:flex items-center gap-1 text-[11px] text-amber-400">
          <AlertTriangle size={11} /> Config. pendiente
        </span>
      ) : (
        <span className="hidden sm:flex items-center gap-1 text-[11px] text-text-muted">
          <WifiOff size={11} /> Sin conectar
        </span>
      )}
      {control}
    </div>
  </div>
);

// ─────────────────────────────────────────────
// Toggle visual
// ─────────────────────────────────────────────
const ToggleSwitch = ({ enabled, onToggle, loading, testId }) => (
  <button
    onClick={onToggle}
    disabled={loading}
    data-testid={testId}
    className={`relative flex h-7 w-12 items-center rounded-full transition-colors duration-200 ${enabled ? 'bg-primary' : 'bg-border-subtle'} ${loading ? 'opacity-50' : 'cursor-pointer'}`}
    aria-pressed={enabled}
  >
    <span className={`absolute h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
  </button>
);

// ─────────────────────────────────────────────
// Sección con título
// ─────────────────────────────────────────────
const Section = ({ title, icon, children }) => (
  <div className="space-y-2">
    <div className="flex items-center gap-2 px-1">
      <span className="text-text-muted">{icon}</span>
      <p className="text-[10px] uppercase tracking-[0.2em] text-text-muted font-semibold">{title}</p>
    </div>
    <div className="space-y-2">{children}</div>
  </div>
);

// ─────────────────────────────────────────────
// Card WhatsApp Embedded Signup
// ─────────────────────────────────────────────
const WhatsAppCard = ({ authFetch, clientId }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        authFetch(`${API}/connections/whatsapp/status`).then(r => r.json ? r.json() : r),
        authFetch(`${API}/connections/whatsapp/config`).then(r => r.json ? r.json() : r),
      ]);
      setStatus(s);
      setConfig(c);
    } catch { setStatus(null); }
    finally { setLoading(false); }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const handleConnect = () => {
    if (!config?.ready) {
      toast.info('WhatsApp Embedded Signup estará disponible cuando Meta apruebe la app de Neuralia369. Proceso en curso — te notificaremos.');
      return;
    }
    // Meta Embedded Signup flow
    if (!window.FB) {
      toast.error('Meta SDK no cargado. Recarga la página.');
      return;
    }
    window.FB.login(async (response) => {
      if (response.authResponse?.code) {
        try {
          const res = await authFetch(`${API}/connections/whatsapp/embedded-signup`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: response.authResponse.code }),
          });
          const json = res.json ? await res.json() : res;
          if (json.success) {
            toast.success('WhatsApp Business conectado ✅');
            await load();
          }
        } catch { toast.error('Error al procesar la conexión'); }
      }
    }, {
      config_id: config.meta_app_id,
      response_type: 'code',
      override_default_response_type: true,
      extras: { setup: {}, featureName: 'whatsapp_embedded_signup' },
    });
  };

  const disconnect = async () => {
    try {
      await authFetch(`${API}/connections/whatsapp`, { method: 'DELETE' });
      toast.success('WhatsApp desconectado');
      await load();
    } catch { toast.error('Error al desconectar'); }
  };

  const isConnected = status?.status === 'connected';
  const isPendingMeta = !config?.ready;

  return (
    <ConnectionCard
      testId="wa-connection-card"
      icon={
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="text-emerald-400">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      }
      name="WhatsApp Business"
      subtitle={isConnected ? `+${status.phone_number_id || 'conectado'}` : 'Mensajes automáticos · Campañas · CRM'}
      status={isConnected ? 'connected' : isPendingMeta ? 'pending_meta' : 'disconnected'}
      badge={isPendingMeta ? 'Config. Meta pendiente' : null}
      control={
        loading ? <RefreshCw size={16} className="animate-spin text-text-muted" /> :
        isConnected ? (
          <button onClick={disconnect} className="rounded-xl border border-border-subtle px-3 py-1.5 text-xs text-text-muted hover:border-rose-400/40 hover:text-rose-400 transition" data-testid="wa-disconnect-btn">
            Desconectar
          </button>
        ) : (
          <button onClick={handleConnect} className="rounded-xl bg-primary px-4 py-1.5 text-xs font-bold text-[#0A1A3F] hover:opacity-90 transition" data-testid="wa-connect-btn">
            Conectar
          </button>
        )
      }
    />
  );
};

// ─────────────────────────────────────────────
// Card Publer (redes sociales)
// ─────────────────────────────────────────────
const PubLerCard = ({ authFetch, isAdmin }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const endpoint = isAdmin ? `${API}/admin/publer/status` : `${API}/publer/status`;
    authFetch(endpoint)
      .then(r => r.json ? r.json() : r)
      .then(setStatus)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [authFetch, isAdmin]);

  const connected = status?.connected;

  return (
    <ConnectionCard
      testId="publer-connection-card"
      icon={<Zap size={18} className="text-purple-400" />}
      name="Publer · Redes Sociales"
      subtitle={connected ? 'TikTok · Instagram · Facebook · LinkedIn' : 'Publicación automática en todas las redes'}
      status={connected ? 'connected' : 'disconnected'}
      control={
        loading ? <RefreshCw size={16} className="animate-spin text-text-muted" /> :
        connected ? (
          <span className="flex items-center gap-1.5 text-[11px] text-emerald-400 font-semibold">
            <CheckCircle2 size={12} /> Activo
          </span>
        ) : (
          <a href="/admin?tab=integrations" className="rounded-xl bg-primary px-4 py-1.5 text-xs font-bold text-[#0A1A3F] hover:opacity-90 transition">
            Configurar
          </a>
        )
      }
    />
  );
};

// ─────────────────────────────────────────────
// Card Nango OAuth (productividad)
// ─────────────────────────────────────────────
const NangoCard = ({ authFetch, provider, label, icon, subtitle, comingSoon = false }) => {
  const [connStatus, setConnStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(!comingSoon);

  // Estado real guardado en backend (embedded_connections vía /connections/hub)
  const loadStatus = useCallback(async () => {
    if (comingSoon) return;
    try {
      const res = await authFetch(`${API}/connections/hub`);
      const d = res.json ? await res.json() : res;
      const card = (d.providers || []).find(c => c.provider === provider);
      setConnStatus(card?.status || 'disconnected');
    } catch { setConnStatus('disconnected'); }
    finally { setChecking(false); }
  }, [authFetch, provider, comingSoon]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Usa el Connect UI de Nango (iframe): solo así llegan los eventos 'connect'
  // con el connectionId, que se confirma en backend para que la conexión quede guardada.
  const connect = async () => {
    setLoading(true);
    try {
      const sessionRes = await authFetch(`${API}/connections/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider }),
      });
      const sessionJson = sessionRes.json ? await sessionRes.json() : sessionRes;
      if (sessionRes.ok === false || !sessionJson?.session_token) {
        throw new Error(sessionJson?.detail || `No se pudo iniciar la conexión de ${label}`);
      }
      const nango = new Nango();
      const connectUi = nango.openConnectUI({
        lang: 'es',
        onEvent: async (evt) => {
          if (evt.type === 'connect') {
            const { connectionId } = evt.payload || {};
            if (connectionId) {
              try {
                const r = await authFetch(`${API}/connections/confirm`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ provider, connection_id: connectionId }),
                });
                if (r.ok === false) throw new Error();
                toast.success(`${label} conectado ✅`);
              } catch { toast.error(`${label} autorizado, pero no se pudo guardar. Inténtalo de nuevo.`); }
            }
            await loadStatus();
          } else if (evt.type === 'close' || evt.type === 'error') {
            await loadStatus();
            setLoading(false);
          }
        },
      });
      connectUi.setSessionToken(sessionJson.session_token);
    } catch (e) {
      toast.error(e?.message || `Error al conectar ${label}`);
      setLoading(false);
    }
  };

  const connected = connStatus === 'connected';

  return (
    <ConnectionCard
      testId={`nango-${provider}-card`}
      icon={icon}
      name={label}
      subtitle={subtitle}
      status={connected ? 'connected' : 'disconnected'}
      control={
        comingSoon ? (
          <span className="text-[11px] text-text-muted font-semibold">Próximamente</span>
        ) :
        checking ? <RefreshCw size={14} className="animate-spin text-text-muted" /> :
        connected ? (
          <span className="flex items-center gap-1 text-[11px] text-emerald-400 font-semibold">
            <CheckCircle2 size={11} /> Activo
          </span>
        ) : (
          <button onClick={connect} disabled={loading}
            className="rounded-xl bg-primary px-4 py-1.5 text-xs font-bold text-[#0A1A3F] disabled:opacity-50 hover:opacity-90 transition"
            data-testid={`nango-${provider}-connect-btn`}>
            {loading ? <RefreshCw size={12} className="animate-spin" /> : (connStatus === 'error' || connStatus === 'expired' ? 'Reconectar' : 'Conectar')}
          </button>
        )
      }
    />
  );
};

// ─────────────────────────────────────────────
// Card Automatización (toggle On/Off)
// ─────────────────────────────────────────────
const AutomationCard = ({ assignment, authFetch, onUpdate, isAdmin, clientId }) => {
  const [toggling, setToggling] = useState(false);

  const toggleEndpoint = isAdmin
    ? `${API}/admin/clients/${clientId}/automations/${assignment.id}/toggle`
    : `${API}/my/automations/${assignment.id}/toggle`;

  const toggle = async () => {
    setToggling(true);
    try {
      await authFetch(toggleEndpoint, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !assignment.is_active }),
      });
      onUpdate?.();
    } catch { toast.error('Error al cambiar estado'); }
    finally { setToggling(false); }
  };

  const icons = { MessageCircle: <MessageCircle size={18} className="text-blue-400" />, Clock: <Clock size={18} className="text-amber-400" />, Trophy: <Trophy size={18} className="text-yellow-400" /> };

  return (
    <ConnectionCard
      testId={`automation-card-${assignment.template_id}`}
      icon={icons[assignment.icon || 'MessageCircle'] || <Zap size={18} className="text-text-muted" />}
      name={assignment.template_name}
      subtitle={assignment.trigger_count > 0 ? `Disparado ${assignment.trigger_count} veces` : 'Sin disparos aún'}
      status={assignment.is_active ? 'connected' : 'disconnected'}
      control={
        <ToggleSwitch
          enabled={assignment.is_active}
          onToggle={toggle}
          loading={toggling}
          testId={`automation-toggle-${assignment.template_id}`}
        />
      }
    />
  );
};

// ─────────────────────────────────────────────
// Automatizaciones completas (asignadas + disponibles)
// ─────────────────────────────────────────────
const AutomationsSection = ({ authFetch, isAdmin, clientId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(null);

  const endpoint = isAdmin && clientId
    ? `${API}/admin/clients/${clientId}/automations`
    : `${API}/my/automations`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(endpoint);
      const json = res.json ? await res.json() : res;
      setData(json);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [authFetch, endpoint]);

  useEffect(() => { load(); }, [load]);

  const assign = async (templateId) => {
    if (!clientId && !isAdmin) return;
    const tid = isAdmin ? `${API}/admin/clients/${clientId}/assign-template/${templateId}` : `${API}/admin/clients/me/assign-template/${templateId}`;
    setAssigning(templateId);
    try {
      const res = await authFetch(tid, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
      const json = res.json ? await res.json() : res;
      if (json.success) { toast.success('Automatización activada ✅'); await load(); }
    } catch { toast.error('Error al activar'); }
    finally { setAssigning(null); }
  };

  if (loading) return <div className="flex justify-center py-6"><RefreshCw size={20} className="animate-spin text-text-muted" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-2">
      {(data.assignments || []).map(a => (
        <AutomationCard key={a.id} assignment={a} authFetch={authFetch} onUpdate={load} isAdmin={isAdmin} clientId={clientId} />
      ))}
      {(data.unassigned_templates || []).map(tpl => {
        const icons = { MessageCircle: <MessageCircle size={18} className="text-blue-400/50" />, Clock: <Clock size={18} className="text-amber-400/50" />, Trophy: <Trophy size={18} className="text-yellow-400/50" /> };
        return (
          <ConnectionCard
            key={tpl.id}
            testId={`automation-unassigned-${tpl.id}`}
            icon={icons[tpl.icon || 'MessageCircle'] || <Zap size={18} className="text-text-muted" />}
            name={tpl.name}
            subtitle={tpl.description}
            status="disconnected"
            control={
              <button onClick={() => assign(tpl.id)} disabled={assigning === tpl.id}
                className="rounded-xl bg-primary px-4 py-1.5 text-xs font-bold text-[#0A1A3F] disabled:opacity-50 hover:opacity-90 transition"
                data-testid={`automation-assign-${tpl.id}`}>
                {assigning === tpl.id ? <RefreshCw size={12} className="animate-spin" /> : 'Activar'}
              </button>
            }
          />
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────
// HUB PRINCIPAL — Vista Cliente
// ─────────────────────────────────────────────
const ClientConnectionsView = ({ authFetch }) => (
  <div className="space-y-6" data-testid="client-connections-hub">
    {/* Resumen de seguridad */}
    <div className="flex items-center gap-2 rounded-2xl border border-primary/20 bg-primary/5 px-4 py-3">
      <Shield size={15} className="text-primary flex-shrink-0" />
      <p className="text-xs text-text-body">Todas las conexiones están cifradas y protegidas. Neuralia369 nunca almacena tus contraseñas.</p>
    </div>

    <Section title="Comunicación" icon={<MessageCircle size={13} />}>
      <WhatsAppCard authFetch={authFetch} />
    </Section>

    <Section title="Automatizaciones" icon={<Zap size={13} />}>
      <AutomationsSection authFetch={authFetch} isAdmin={false} />
    </Section>

    <Section title="Redes Sociales" icon={<Share2 size={13} />}>
      <PubLerCard authFetch={authFetch} isAdmin={false} />
    </Section>

    <Section title="Productividad" icon={<Wifi size={13} />}>
      <NangoCard authFetch={authFetch} provider="google-calendar" comingSoon label="Google Calendar" subtitle="Agenda y reuniones" icon={<span className="text-blue-400 font-bold text-sm">G</span>} />
      <NangoCard authFetch={authFetch} provider="gmail" label="Gmail" subtitle="Correos automáticos" icon={<span className="text-red-400 font-bold text-sm">M</span>} />
      <NangoCard authFetch={authFetch} provider="google-drive" comingSoon label="Google Drive" subtitle="Documentos y contratos" icon={<span className="text-green-400 font-bold text-sm">D</span>} />
    </Section>
  </div>
);

// Redes sociales para admin
const Share2 = ({ size, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
  </svg>
);

// ─────────────────────────────────────────────
// HUB PRINCIPAL — Vista Admin (con selector de cliente)
// ─────────────────────────────────────────────
const AdminConnectionsView = ({ authFetch }) => {
  const [clients, setClients] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [loadingClients, setLoadingClients] = useState(true);

  useEffect(() => {
    authFetch(`${API}/admin/partners`)
      .then(r => r.json ? r.json() : r)
      .then(d => {
        const list = d.partners || d;
        setClients(Array.isArray(list) ? list : []);
      })
      .catch(() => {})
      .finally(() => setLoadingClients(false));
  }, [authFetch]);

  return (
    <div className="space-y-6" data-testid="admin-connections-hub">
      {/* Selector de cliente */}
      <div className="glass-card p-5">
        <div className="flex items-center gap-3 mb-4">
          <Users size={16} className="text-primary" />
          <p className="font-semibold text-text-heading">Gestionar conexiones de cliente</p>
        </div>
        {loadingClients ? (
          <div className="flex items-center gap-2 text-text-muted text-sm"><RefreshCw size={14} className="animate-spin" /> Cargando clientes...</div>
        ) : (
          <div className="relative">
            <select
              value={selectedId}
              onChange={e => setSelectedId(e.target.value)}
              className="w-full appearance-none rounded-xl border border-border-subtle bg-background px-4 py-3 pr-10 text-sm text-text-primary focus:border-primary focus:outline-none"
              data-testid="admin-client-selector"
            >
              <option value="">— Selecciona un cliente —</option>
              {clients.map(c => (
                <option key={c.id || c._id} value={c.id || c._id}>{c.name || c.email}</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
          </div>
        )}
      </div>

      {selectedId && (
        <div className="space-y-6">
          <div className="flex items-center gap-2 rounded-2xl border border-amber-400/20 bg-amber-500/5 px-4 py-3">
            <Shield size={14} className="text-amber-400" />
            <p className="text-xs text-amber-300">Gestionando conexiones de cliente ID: <span className="font-mono font-bold">{selectedId}</span></p>
          </div>

          <Section title="Comunicación" icon={<MessageCircle size={13} />}>
            <WhatsAppCard authFetch={authFetch} clientId={selectedId} />
          </Section>

          <Section title="Automatizaciones" icon={<Zap size={13} />}>
            <AutomationsSection authFetch={authFetch} isAdmin={true} clientId={selectedId} />
          </Section>

          <Section title="Redes Sociales" icon={<Share2 size={13} />}>
            <PubLerCard authFetch={authFetch} isAdmin={true} />
          </Section>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// EXPORT PRINCIPAL
// ─────────────────────────────────────────────
export const EmbeddedConnectionsHub = ({ authFetch, mode = 'client' }) => {
  if (mode === 'admin') return <AdminConnectionsView authFetch={authFetch} />;
  return <ClientConnectionsView authFetch={authFetch} />;
};

export default EmbeddedConnectionsHub;

// ─────────────────────────────────────────────
// Exports adicionales para IntegrationsCenterHub
// ─────────────────────────────────────────────

export const PublerConnectionCard = ({ authFetch, isAdmin }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [key, setKey] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = isAdmin ? `${API}/admin/publer/status` : `${API}/publer/status`;
      const res = await authFetch(endpoint);
      const json = res.json ? await res.json() : res;
      setStatus(json);
    } catch { setStatus(null); }
    finally { setLoading(false); }
  }, [authFetch, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!key.trim()) return;
    setSaving(true);
    try {
      const res = await authFetch(`${API}/admin/publer/connect`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ api_key: key.trim(), workspace: 'neuralia' }),
      });
      const json = res.json ? await res.json() : res;
      if (json.success) { toast.success('Publer conectado ✅'); setShowForm(false); await load(); }
      else toast.error(json.detail || 'Error');
    } catch { toast.error('Error al conectar'); }
    finally { setSaving(false); }
  };

  const connected = status?.connected;
  return (
    <div className="glass-card p-5 space-y-3" data-testid="publer-admin-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-purple-500/20"><Zap size={16} className="text-purple-400" /></div>
          <div><p className="font-bold text-sm text-text-heading">Publer</p><p className="text-[11px] text-text-muted">TikTok · Instagram · Facebook</p></div>
        </div>
        {loading ? <RefreshCw size={14} className="animate-spin text-text-muted" /> :
          connected ? <span className="flex items-center gap-1 text-[11px] text-emerald-400 font-semibold"><CheckCircle2 size={11} /> Conectado</span> :
          <span className="text-[11px] text-amber-400">Sin conectar</span>}
      </div>
      {connected && status?.note && <p className="text-[11px] text-text-muted border border-border-subtle rounded-xl px-3 py-2">{status.note}</p>}
      {!connected && (
        showForm ? (
          <div className="space-y-2">
            <input type="password" value={key} onChange={e => setKey(e.target.value)} placeholder="API Key de Publer (app.publer.io → Settings → API)"
              className="w-full rounded-xl border border-border-subtle bg-background px-3 py-2 text-xs font-mono text-text-primary focus:border-primary focus:outline-none" data-testid="publer-key-input" />
            <div className="flex gap-2">
              <button onClick={() => setShowForm(false)} className="rounded-xl border border-border-subtle px-3 py-1.5 text-xs text-text-muted">Cancelar</button>
              <button onClick={save} disabled={saving || !key.trim()} className="flex items-center gap-1.5 rounded-xl bg-primary px-4 py-1.5 text-xs font-bold text-[#0A1A3F] disabled:opacity-50" data-testid="publer-save-btn">
                {saving ? <RefreshCw size={11} className="animate-spin" /> : null} Guardar
              </button>
            </div>
          </div>
        ) : (
          <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-1.5 text-xs font-bold text-[#0A1A3F]" data-testid="publer-connect-btn">
            <Key size={11} /> Conectar Publer
          </button>
        )
      )}
    </div>
  );
};

export const WatiConnectionCard = ({ authFetch }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [form, setForm] = useState({ endpoint: '', token: '', phone: '' });
  const [testPhone, setTestPhone] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/admin/wati/status`);
      const json = res.json ? await res.json() : res;
      setStatus(json);
      if (json.endpoint) setForm(f => ({ ...f, endpoint: json.endpoint, phone: json.phone || '' }));
    } catch { setStatus(null); }
    finally { setLoading(false); }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.endpoint || !form.token) return;
    setSaving(true);
    try {
      const res = await authFetch(`${API}/admin/wati/config`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      const json = res.json ? await res.json() : res;
      if (json.success) { toast.success('WATI configurado ✅'); setShowForm(false); await load(); }
      else toast.error(json.detail || 'Error');
    } catch { toast.error('Error al guardar WATI'); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testPhone) return;
    setTesting(true);
    try {
      const res = await authFetch(`${API}/admin/wati/test`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ phone: testPhone }) });
      const json = res.json ? await res.json() : res;
      if (json.success) toast.success('Mensaje de prueba enviado ✅');
      else toast.error(json.detail || 'Error');
    } catch { toast.error('Error al enviar test'); }
    finally { setTesting(false); }
  };

  const isConfigured = status?.configured;

  return (
    <div className="glass-card p-5 space-y-3" data-testid="wati-admin-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-green-500/20">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-green-400">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          </div>
          <div><p className="font-bold text-sm text-text-heading">WATI · WhatsApp Business</p><p className="text-[11px] text-text-muted">Mensajes automáticos · Campañas · Leads</p></div>
        </div>
        {loading ? <RefreshCw size={14} className="animate-spin text-text-muted" /> :
          isConfigured ? <span className="flex items-center gap-1 text-[11px] text-emerald-400 font-semibold"><CheckCircle2 size={11} /> Configurado</span> :
          <span className="text-[11px] text-amber-400">Sin configurar</span>}
      </div>

      {isConfigured ? (
        <div className="space-y-2">
          <p className="text-[11px] text-text-muted font-mono">{status.endpoint}</p>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setShowForm(true)} className="rounded-xl border border-border-subtle px-3 py-1.5 text-xs text-text-muted hover:border-primary/40 transition" data-testid="wati-edit-btn"><Key size={11} /> Editar</button>
          </div>
          <div className="border-t border-border-subtle pt-2">
            <div className="flex gap-2">
              <input type="text" value={testPhone} onChange={e => setTestPhone(e.target.value)} placeholder="51987654321 (sin +)"
                className="flex-1 rounded-xl border border-border-subtle bg-background px-3 py-1.5 text-xs font-mono text-text-primary focus:border-primary focus:outline-none" />
              <button onClick={sendTest} disabled={testing || !testPhone} className="flex items-center gap-1 rounded-xl bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50" data-testid="wati-test-btn">
                {testing ? <RefreshCw size={11} className="animate-spin" /> : <Zap size={11} />} Test
              </button>
            </div>
          </div>
        </div>
      ) : (
        !showForm ? (
          <div className="space-y-2">
            <p className="text-xs text-text-muted">BSP oficial Meta. Activa WhatsApp Business por llamada de voz sin depender de SMS ni de aprobación de Meta directamente.</p>
            <button onClick={() => setShowForm(true)} className="flex items-center gap-1.5 rounded-xl bg-primary px-4 py-1.5 text-xs font-bold text-[#0A1A3F]" data-testid="wati-configure-btn">
              <Key size={11} /> Configurar WATI
            </button>
          </div>
        ) : null
      )}

      {showForm && (
        <div className="space-y-2 pt-1">
          <input type="text" value={form.endpoint} onChange={e => setForm(f => ({...f, endpoint: e.target.value}))} placeholder="https://live-mt-server.wati.io/TENANT"
            className="w-full rounded-xl border border-border-subtle bg-background px-3 py-2 text-xs font-mono text-text-primary focus:border-primary focus:outline-none" data-testid="wati-endpoint-input" />
          <input type="password" value={form.token} onChange={e => setForm(f => ({...f, token: e.target.value}))} placeholder="Bearer token WATI"
            className="w-full rounded-xl border border-border-subtle bg-background px-3 py-2 text-xs font-mono text-text-primary focus:border-primary focus:outline-none" data-testid="wati-token-input" />
          <input type="text" value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} placeholder="Número canal (ej: 51999999999)"
            className="w-full rounded-xl border border-border-subtle bg-background px-3 py-2 text-xs font-mono text-text-primary focus:border-primary focus:outline-none" data-testid="wati-phone-input" />
          <div className="flex gap-2">
            <button onClick={() => setShowForm(false)} className="rounded-xl border border-border-subtle px-3 py-1.5 text-xs text-text-muted">Cancelar</button>
            <button onClick={save} disabled={saving || !form.endpoint || !form.token} className="flex items-center gap-1.5 rounded-xl bg-primary px-4 py-1.5 text-xs font-bold text-[#0A1A3F] disabled:opacity-50" data-testid="wati-save-btn">
              {saving ? <RefreshCw size={11} className="animate-spin" /> : null} {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const MakeComCard = ({ authFetch }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authFetch(`${API}/admin/webhooks/make`)
      .then(r => r.json ? r.json() : r)
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [authFetch]);

  const webhooks = data?.webhooks || [];
  const connected = webhooks.length > 0;

  return (
    <div className="glass-card p-5 space-y-3" data-testid="makecom-connection-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-500/20"><Zap size={16} className="text-amber-400" /></div>
          <div><p className="font-bold text-sm text-text-heading">Make.com</p><p className="text-[11px] text-text-muted">Automatización · {webhooks.length} webhook{webhooks.length !== 1 ? 's' : ''} activo{webhooks.length !== 1 ? 's' : ''}</p></div>
        </div>
        {loading ? <RefreshCw size={14} className="animate-spin text-text-muted" /> :
          connected ? <span className="flex items-center gap-1 text-[11px] text-emerald-400 font-semibold"><CheckCircle2 size={11} /> {webhooks.length} webhook{webhooks.length !== 1 ? 's' : ''}</span> :
          <span className="text-[11px] text-amber-400">Pendiente</span>}
      </div>
      <a href="/admin?tab=webhooks_make" target="_blank" rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-1.5 text-xs font-bold text-[#0A1A3F] hover:opacity-90 transition">
        <Zap size={11} /> {connected ? 'Gestionar webhooks' : 'Configurar Make.com'}
      </a>
    </div>
  );
};
